import React from 'react';
import styled from '@emotion/styled';
import { Checkbox } from '@kompare/kompare-ui-library';
import { Flex } from 'src/components';
import { colors } from 'src/theme';
import { Text } from '../../typography';
import type {
  QuestionnaireAnswers,
  TableAnswer,
  TableQuestion,
  TableSelectionMode,
} from './healthQuestionnaire.types';
import { getTableMeta } from './healthQuestionnaire.types';

const TableWrap = styled.div({
  width: '100%',
  overflowX: 'auto',
});

const TableGrid = styled.div<{ $columns: number }>(({ $columns }) => ({
  display: 'grid',
  gridTemplateColumns: `minmax(160px, 2fr) repeat(${$columns}, minmax(72px, 1fr))`,
  alignItems: 'stretch',
  border: `1px solid ${colors.border5}`,
  borderRadius: 8,
  overflow: 'hidden',
  minWidth: 'fit-content',
}));

const HeaderCell = styled.div({
  padding: '12px 8px',
  backgroundColor: '#F2EAF7',
  borderBottom: `1px solid ${colors.border5}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
});

const RowHeaderCell = styled.div<{ $hasError: boolean }>(({ $hasError }) => ({
  padding: '10px 12px',
  borderTop: `1px solid ${colors.border6}`,
  backgroundColor: $hasError ? '#FCEFEF' : '#FFFFFF',
  display: 'flex',
  alignItems: 'center',
}));

const Cell = styled.div<{ $hasError: boolean }>(({ $hasError }) => ({
  padding: '10px 8px',
  borderTop: `1px solid ${colors.border6}`,
  borderLeft: `1px solid ${colors.border6}`,
  backgroundColor: $hasError ? '#FCEFEF' : '#FFFFFF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ErrorText = styled.span({
  fontSize: 13,
  color: colors.error1,
  fontWeight: 500,
});

interface TableQuestionProps {
  question: TableQuestion;
  answers: QuestionnaireAnswers;
  onChange: (next: QuestionnaireAnswers) => void;
  hasError: boolean;
}

function applyToggle(
  mode: TableSelectionMode | string | null | undefined,
  current: string[],
  columnValue: string,
): string[] {
  if (mode === 'single_per_row') {
    return current.includes(columnValue) ? [] : [columnValue];
  }
  return current.includes(columnValue)
    ? current.filter((v) => v !== columnValue)
    : [...current, columnValue];
}

export const TableQuestionSection: React.FC<TableQuestionProps> = ({
  question,
  answers,
  onChange,
  hasError,
}) => {
  const { code, title, description } = question;
  const meta = getTableMeta(question);
  const columns = meta.columns ?? [];
  const rows = meta.rows ?? [];
  const selectionMode = meta.selectionMode ?? 'multiple_per_row';
  const table: TableAnswer = answers.tables[code] ?? {};

  const handleToggle = (rowCode: string, columnValue: string) => {
    const rowSelected = table[rowCode] ?? [];
    const nextRow = applyToggle(selectionMode, rowSelected, columnValue);
    const nextTable: TableAnswer = { ...table };
    if (nextRow.length === 0) {
      delete nextTable[rowCode];
    } else {
      nextTable[rowCode] = nextRow;
    }
    onChange({
      ...answers,
      tables: { ...answers.tables, [code]: nextTable },
    });
  };

  return (
    <Flex vertical gap={16}>
      {title && <Text type="large" bold>{title}</Text>}
      {description && <Text type="base">{description}</Text>}

      <TableWrap>
        <TableGrid $columns={columns.length || 1}>
          <HeaderCell />
          {columns.map((col) => (
            <HeaderCell key={col.value}>
              <Text type="small" bold>{col.label}</Text>
            </HeaderCell>
          ))}

          {rows.map((row) => {
            const rowSelected = table[row.code] ?? [];
            const rowHasError = hasError && rowSelected.length === 0;
            return (
              <React.Fragment key={row.code}>
                <RowHeaderCell $hasError={rowHasError}>
                  <Text type="base">{row.label}</Text>
                </RowHeaderCell>
                {columns.map((col) => (
                  <Cell key={col.value} $hasError={rowHasError}>
                    <Checkbox
                      checked={rowSelected.includes(col.value)}
                      onChange={() => handleToggle(row.code, col.value)}
                      checkboxSize="small"
                    />
                  </Cell>
                ))}
              </React.Fragment>
            );
          })}
        </TableGrid>
      </TableWrap>

      {hasError && <ErrorText>Odaberite odgovor u svakom redu</ErrorText>}
    </Flex>
  );
};
